import { useEffect, useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/toast';

import { ENTITY_COPY, savedChanges } from '../copy';
import {
  areaTree,
  catalogError,
  codeFragmentError,
  descendantIds,
  requiredTextError,
} from '../presentation';
import { effectiveProcessType, PROCESS_TYPES, rootOf } from '../coding';
import type { ProcessArea } from '../types';
import { useSaveCatalogItem } from '../use-catalogs-admin';
import { Select } from '@/components/ui/Select';

const COPY = ENTITY_COPY.process_area;
const ROOT = '';

interface Form {
  codeFragment: string;
  name: string;
  parentId: string;
  /** Tipo de proceso 01 a 04 (G3-2); '' = sin definir. Solo en un proceso principal. */
  processType: string;
}

const TYPE_OPTIONS = [
  { value: '', label: 'Sin definir' },
  ...PROCESS_TYPES.map((t) => ({ value: String(t.value), label: t.label })),
];

/** Alta o edición de un proceso. El padre se elige en el árbol de procesos activos;
 *  al editar no se ofrecen el propio proceso ni sus subprocesos (evita ciclos), y un
 *  padre actual desactivado se conserva visible para no perderlo al guardar. */
export function ProcessAreaModal({
  open,
  editing,
  areas,
  onClose,
}: {
  open: boolean;
  editing: ProcessArea | null;
  areas: readonly ProcessArea[];
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const parentFieldId = useId();
  const typeFieldId = useId();
  const typeHintId = useId();
  const save = useSaveCatalogItem('process-areas');
  const { showToast } = useToast();
  const [form, setForm] = useState<Form>({
    codeFragment: '',
    name: '',
    parentId: ROOT,
    processType: '',
  });
  const [checked, setChecked] = useState(false);
  const [server, setServer] = useState<ReturnType<typeof catalogError> | null>(null);

  useEffect(() => {
    if (open) {
      setForm({
        codeFragment: editing?.codeFragment ?? '',
        name: editing?.name ?? '',
        parentId: editing?.parentId ?? ROOT,
        processType:
          editing === null || editing.processType === null ? '' : String(editing.processType),
      });
      setChecked(false);
      setServer(null);
    }
  }, [open, editing]);

  const parents = useMemo(() => {
    const excluded = editing === null ? new Set<string>() : descendantIds(areas, editing.id);
    return areaTree(areas).filter(
      (r) => !excluded.has(r.area.id) && (r.area.isActive || r.area.id === editing?.parentId),
    );
  }, [areas, editing]);

  // Un subproceso hereda el tipo de su proceso principal (04-GC-P08 §B.1): se muestra,
  // no se edita.
  const isRoot = form.parentId === ROOT;
  const parentArea = isRoot ? undefined : areas.find((a) => a.id === form.parentId);
  const inheritedRoot = parentArea === undefined ? null : rootOf(parentArea, areas);
  const inherited = parentArea === undefined ? null : effectiveProcessType(parentArea, areas);
  const ownType = form.processType === '' ? null : Number(form.processType);

  const set = (patch: Partial<Form>): void => {
    setForm((f) => ({ ...f, ...patch }));
    setServer(null);
  };
  // El fragmento solo se valida si cambió: un proceso importado con un fragmento
  // legado (con separador o largo) se puede renombrar o mover sin tocarlo.
  // Se compara lo escrito tal cual: si el usuario no tocó el campo, un fragmento
  // antiguo en minúsculas no se reescribe ni bloquea el guardado.
  const codeChanged = editing === null || form.codeFragment !== editing.codeFragment;
  const codeInvalid = codeChanged && codeFragmentError(form.codeFragment) !== null;
  const codeMsg =
    server?.field === 'code'
      ? server.message
      : checked && codeChanged
        ? codeFragmentError(form.codeFragment)
        : null;
  const nameMsg =
    server?.field === 'name'
      ? server.message
      : checked
        ? requiredTextError(form.name, 200, 'Escribe el nombre del proceso')
        : null;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setChecked(true);
    if (codeInvalid || requiredTextError(form.name, 200, '') !== null || save.isPending) {
      return;
    }
    const name = form.name.trim();
    const parentId = form.parentId === ROOT ? null : form.parentId;
    const codeFragment = form.codeFragment.trim().toUpperCase();
    // Alta: todo. Edición: solo lo que cambió (sin cambios, se cierra sin llamar al API).
    // El tipo propio solo vive en el principal: al volverlo subproceso se le quita.
    const processType = isRoot ? ownType : null;
    const body: Record<string, unknown> =
      editing === null
        ? { codeFragment, name, parentId, ...(isRoot ? { processType } : {}) }
        : {
            ...(codeChanged ? { codeFragment } : {}),
            ...(name !== editing.name ? { name } : {}),
            ...(parentId !== editing.parentId ? { parentId } : {}),
            ...(processType !== editing.processType ? { processType } : {}),
          };
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      await save.mutateAsync({ id: editing?.id ?? null, body });
      showToast(editing === null ? COPY.created(name) : savedChanges(name));
      onClose();
    } catch (err) {
      setServer(catalogError(err, 'process_area'));
    }
  }

  return (
    <ModalShell
      open={open}
      title={editing === null ? COPY.newTitle : COPY.editTitle}
      busy={save.isPending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn sm" disabled={save.isPending}>
            {save.isPending
              ? 'Guardando…'
              : editing === null
                ? COPY.createLabel
                : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <TextField
          label="Fragmento de código"
          value={form.codeFragment}
          maxLength={8}
          placeholder="PD"
          mono
          hint={
            editing === null
              ? 'Letras y números, hasta 8. Único entre los procesos del mismo nivel.'
              : 'Letras y números, hasta 8. Cambiarlo no cambia los códigos de los documentos ya emitidos.'
          }
          error={codeMsg}
          onChange={(codeFragment) => set({ codeFragment })}
        />
        <TextField
          label="Nombre"
          value={form.name}
          maxLength={200}
          placeholder="Producción"
          error={nameMsg}
          onChange={(name) => set({ name })}
        />
        <div className="fld">
          <label htmlFor={parentFieldId} className="fld-lab">
            Pertenece a
          </label>
          <Select
            id={parentFieldId}
            value={form.parentId}
            options={[
              { value: ROOT, label: 'Ninguno (proceso principal)' },
              ...parents.map(({ area, path }) => ({
                value: area.id,
                label: `${path} · ${area.name}${area.isActive ? '' : ' (desactivado)'}`,
              })),
            ]}
            onChange={(v) => set({ parentId: v })}
          />
        </div>
        <div className="fld">
          <label htmlFor={typeFieldId} className="fld-lab">
            Tipo de proceso
          </label>
          <Select
            id={typeFieldId}
            value={isRoot ? form.processType : inherited === null ? '' : String(inherited)}
            options={TYPE_OPTIONS}
            disabled={!isRoot}
            ariaDescribedBy={typeHintId}
            onChange={(v) => set({ processType: v })}
          />
          <span id={typeHintId} className="fld-hint">
            {isRoot
              ? 'Primer número del código de sus documentos (04-GC-P08). Sin él no se propone el código.'
              : `Lo hereda de ${inheritedRoot?.name ?? 'su proceso principal'}`}
          </span>
        </div>
        {server !== null && server.field === null && (
          <p className="fld-error" role="alert">
            {server.message}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
